import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input, Table, Space, Select, Typography, Menu, Tooltip } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Button, Modal, Dropdown } from "react-bootstrap";
import {
  manualOptions,
  backendOptions,
  filterOptions,
} from "../../entities/Constants/DashboardConstants";
import {
  deleteLeads,
  fetchLeads,
  filterLeads,
  setToastForAddLead,
  updateLeadStatus,
  updatePagination,
  updateSortOrder,
} from "../../features/Lead/leadsSlice";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { formatDateTime } from "../../utils/Utils";
import { resetToastConfig } from "../../features/Lead/leadsSlice";
import ToastNotification from "../../components/Common/ToastNotification";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import appInsights from "../../AppInsights";

const { Option } = Select;
const { Text } = Typography;

const LeadPage = () => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const { profileData } = useSelector((state) => state.profile);
  const { toastConfig } = useSelector((state) => state.leads);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const coachId = profileData?.coachId;

  const { leads, loading, pagination, sortOrder, totalCount, error } =
    useSelector((state) => state.leads);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [deleteType, setDeleteType] = useState("");
  const [recordToDelete, setRecordToDelete] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [statusUpdates, setStatusUpdates] = useState({});

  // Track page load time
  const pageLoadTime = Date.now();

  // Track page view on component mount
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackPageView({
      name: "Lead Management Page",
      properties: {
        openedAt: openTime,
        coachId: coachId,
        totalLeads: totalCount || 0,
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
      },
    });

    // Track page load event
    appInsights.trackEvent({
      name: "LeadPage_Loaded",
      properties: {
        coachId: coachId,
        timestamp: openTime,
        totalLeads: totalCount || 0,
      },
    });
  }, []);

  // Track total time spent on lead page
  const trackTotalTimeSpent = () => {
    const totalTimeSec = ((Date.now() - pageLoadTime) / 1000).toFixed(2);
    const totalTimeMinutes = (parseFloat(totalTimeSec) / 60).toFixed(2);
    
    appInsights.trackEvent({
      name: "LeadPage_Time_Spent",
      properties: {
        coachId: coachId,
        totalTimeSeconds: parseFloat(totalTimeSec),
        totalTimeFormatted: `${totalTimeSec}s`,
        totalTimeMinutes: parseFloat(totalTimeMinutes),
        totalLeads: totalCount || 0,
        searchQueryUsed: !!searchQuery,
        statusFilterUsed: !!statusFilter,
        selectedRowsCount: selectedRowKeys.length,
        actionsPerformed: getActionCount(),
        pageLoadTimestamp: new Date(pageLoadTime).toISOString(),
        pageExitTimestamp: new Date().toISOString(),
        sessionSummary: getSessionSummary(),
      },
    });
  };

  // Helper function to count actions performed during the session
  const getActionCount = () => {
    // This would track various actions - you can implement more detailed tracking
    const actions = {
      searches: searchQuery ? 1 : 0,
      statusChanges: Object.keys(statusUpdates).length,
      deletions: 0, // You can track this separately
      edits: 0, // You can track this separately
      filtersApplied: statusFilter ? 1 : 0,
      sortChanges: 0, // You can track this separately
    };
    return actions;
  };

  // Helper function to get session summary
  const getSessionSummary = () => {
    return {
      leadsViewed: leads.length,
      leadsSelected: selectedRowKeys.length,
      searchUsed: !!searchQuery,
      filterUsed: !!statusFilter,
      timeSpent: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`
    };
  };

  // Track page unload
  useEffect(() => {
    // Track beforeunload (when user closes tab/browser)
    const handleBeforeUnload = () => {
      trackTotalTimeSpent();
    };

    // Track visibility change (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTotalTimeSpent();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Track when component unmounts (navigation to other pages)
      trackTotalTimeSpent();
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [coachId, totalCount, searchQuery, statusFilter, selectedRowKeys.length]);

  // Fetch leads on component mount and when state changes
  useEffect(() => {
    if (coachId) {
      const delay = location.state?.fromFreeClient ? 9000 : 0;
      
      const timer = setTimeout(() => {
        appInsights.trackEvent({
          name: "LeadPage_Leads_Fetch_Attempt",
          properties: {
            coachId: coachId,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize,
            sortOrder: sortOrder,
          },
        });

        dispatch(
          fetchLeads({
            coachId: coachId,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize,
            sortOrder,
          })
        );
      }, delay);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, sortOrder, coachId]);

  // Track successful leads load
  useEffect(() => {
    if (!loading && leads && coachId) {
      appInsights.trackEvent({
        name: "LeadPage_Leads_Loaded",
        properties: {
          coachId: coachId,
          totalLeads: totalCount,
          currentPage: pagination.current,
          pageSize: pagination.pageSize,
          hasLeads: leads.length > 0,
          leadsCount: leads.length,
          loadTime: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
        },
      });
    }
  }, [loading, leads, coachId, totalCount, pagination]);

  useEffect(() => {
    if (toastConfig && toastConfig.type === "success") {
      const timer = setTimeout(() => {
        dispatch(resetToastConfig());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastConfig, dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (coachId) {
        // Track search activity
        if (searchQuery) {
          appInsights.trackEvent({
            name: "LeadPage_Search_Performed",
            properties: {
              coachId: coachId,
              searchQuery: searchQuery,
              statusFilter: statusFilter,
              currentPage: pagination.current,
              timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
            },
          });
        }

        dispatch(
          filterLeads({
            coachId,
            name: searchQuery,
            status: statusFilter,
            sortOrder,
          })
        );
      }
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    statusFilter,
    pagination.current,
    pagination.pageSize,
    sortOrder,
    dispatch,
  ]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSortChange = ({ key }) => {
    // Track sort change
    appInsights.trackEvent({
      name: "LeadPage_Sort_Changed",
      properties: {
        coachId: coachId,
        previousSortOrder: sortOrder,
        newSortOrder: key,
        sortType: key === "desc" ? "newest" : "oldest",
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    if (coachId) {
      dispatch(updateSortOrder(key));
      dispatch(
        filterLeads({
          coachId,
          name: debouncedSearch,
          status: statusFilter,
          sortOrder: key,
        })
      );
    }
  };

  const handleRowClick = (record, e) => {
    if (
      e.target.closest(".action-buttons") ||
      e.target.closest(".ant-select")
    ) {
      return;
    }

    // Track lead view
    appInsights.trackEvent({
      name: "LeadPage_Lead_Viewed",
      properties: {
        coachId: coachId,
        leadId: record.key,
        leadName: record.name,
        leadStatus: record.status,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    navigate(`/leads/editLead/${record.key}`, {
      state: { ...record, isReadOnly: record.status === "Won" },
    });
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;

    // Track pagination change
    appInsights.trackEvent({
      name: "LeadPage_Pagination_Changed",
      properties: {
        coachId: coachId,
        previousPage: pagination.current,
        newPage: current,
        previousPageSize: pagination.pageSize,
        newPageSize: pageSize,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    dispatch(
      updatePagination({
        current,
        pageSize,
      })
    );
    dispatch(
      filterLeads({
        coachId: coachId,
        name: debouncedSearch,
        status: statusFilter,
      })
    );
  };

  const handleStatusFilterChange = (e) => {
    const filterKey = e.key === "All" ? "" : e.key;
    
    // Track status filter change
    appInsights.trackEvent({
      name: "LeadPage_Status_Filter_Changed",
      properties: {
        coachId: coachId,
        previousFilter: statusFilter,
        newFilter: filterKey,
        filterLabel: e.key === "All" ? "All" : filterKey,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    setStatusFilter(filterKey);

    dispatch(
      updatePagination({
        current: 1,
      })
    );

    dispatch(
      filterLeads({
        coachId,
        name: debouncedSearch,
        status: filterKey,
        pageNumber: 1,
        pageSize: pagination.pageSize,
        sortOrder,
      })
    );
  };

  const handleStatusChange = async (value, recordKey) => {
    // Track status update attempt
    appInsights.trackEvent({
      name: "LeadPage_Status_Update_Attempt",
      properties: {
        coachId: coachId,
        leadId: recordKey,
        newStatus: value,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    try {
      await dispatch(
        updateLeadStatus({ coachId, leadId: recordKey, status: value })
      ).unwrap();

      // Track successful status update
      appInsights.trackEvent({
        name: "LeadPage_Status_Update_Success",
        properties: {
          coachId: coachId,
          leadId: recordKey,
          newStatus: value,
        },
      });

      setStatusFilter("");

      dispatch(updatePagination({ current: 1 }));

      dispatch(
        fetchLeads({
          coachId: coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder,
        })
      );
    } catch (error) {
      // Track status update failure
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "LeadPage_Status_Update_Failed",
          coachId: coachId,
          leadId: recordKey,
          newStatus: value,
          error: error.message,
        },
      });

      dispatch(
        setToastForAddLead({
          type: "error",
          message: trans("LeadPage.statusUpdateFailed"),
          heading: trans("Common.failed"),
        })
      );
    }
  };

  const handleDeleteClick = (record, e) => {
    e.stopPropagation();
    
    // Track delete initiation
    appInsights.trackEvent({
      name: "LeadPage_Delete_Initiated",
      properties: {
        coachId: coachId,
        leadId: record.key,
        leadName: record.name,
        deleteType: "single",
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    setRecordToDelete([record.key]);
    setDeleteType("single");
    setShowModal(true);
  };

  const handleMultipleDeleteClick = () => {
    // Track bulk delete initiation
    appInsights.trackEvent({
      name: "LeadPage_Bulk_Delete_Initiated",
      properties: {
        coachId: coachId,
        selectedCount: selectedRowKeys.length,
        deleteType: "multiple",
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });

    setRecordToDelete(selectedRowKeys);
    setDeleteType("multiple");
    setShowModal(true);
  };

  const handleModalClose = () => {
    // Track modal cancellation
    appInsights.trackEvent({
      name: "LeadPage_Delete_Cancelled",
      properties: {
        coachId: coachId,
        deleteType: deleteType,
        recordsCount: recordToDelete?.length || 0,
      },
    });

    setShowModal(false);
    setRecordToDelete(null);
    setDeleteType("");
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete || recordToDelete.length === 0) return;

    // Track delete confirmation
    appInsights.trackEvent({
      name: "LeadPage_Delete_Confirmed",
      properties: {
        coachId: coachId,
        deleteType: deleteType,
        recordsCount: recordToDelete.length,
        records: recordToDelete,
      },
    });

    try {
      await dispatch(deleteLeads(recordToDelete)).unwrap();

      // Track successful deletion
      appInsights.trackEvent({
        name: "LeadPage_Delete_Success",
        properties: {
          coachId: coachId,
          deleteType: deleteType,
          recordsCount: recordToDelete.length,
        },
      });

      dispatch(
        setToastForAddLead({
          type: "success",
          message: trans(
            recordToDelete.length === 1
              ? "LeadPage.singleLeadDeleted"
              : "LeadPage.multipleLeadsDeleted"
          ),
          heading: trans("Common.success"),
        })
      );
      setStatusFilter("");
      dispatch(updatePagination({ current: 1 }));

      dispatch(
        fetchLeads({
          coachId: coachId,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder,
        })
      );
    } catch (error) {
      // Track deletion failure
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "LeadPage_Delete_Failed",
          coachId: coachId,
          deleteType: deleteType,
          recordsCount: recordToDelete.length,
          error: error.message,
        },
      });

      dispatch(
        setToastForAddLead({
          type: "error",
          message: trans("LeadPage.deleteFailed"),
          heading: trans("Common.failed"),
        })
      );
    }

    if (deleteType === "multiple") {
      setSelectedRowKeys([]);
    }
    setShowModal(false);
    setRecordToDelete(null);
    setDeleteType("");
  };

  const onSelectChange = (selectedKeys) => {
    // Track row selection
    if (selectedKeys.length !== selectedRowKeys.length) {
      appInsights.trackEvent({
        name: "LeadPage_Row_Selection_Changed",
        properties: {
          coachId: coachId,
          selectedCount: selectedKeys.length,
          previousSelectedCount: selectedRowKeys.length,
          action: selectedKeys.length > selectedRowKeys.length ? "selected" : "deselected",
          timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
        },
      });
    }

    setSelectedRowKeys(selectedKeys);
  };

  // Track navigation to add new lead
  const handleAddNewLead = () => {
    appInsights.trackEvent({
      name: "LeadPage_Add_New_Lead_Clicked",
      properties: {
        coachId: coachId,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });
    navigate("addNewLead");
  };

  // Track navigation to send offer
  const handleSendOffer = () => {
    appInsights.trackEvent({
      name: "LeadPage_Send_Offer_Clicked",
      properties: {
        coachId: coachId,
        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
      },
    });
    navigate("addNewOffer");
  };

  const menuItems = filterOptions.map((option) => ({
    key: option.value,
    label: option.label,
  }));

  const columns = [
    {
      title: trans("Common.name"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <span>
          {record.name?.charAt(0)?.toUpperCase() +
            record.name?.slice(1)?.toLowerCase()}
        </span>
      ),
    },
    { title: trans("Common.phoneNumber"), dataIndex: "phone", key: "phone" },
    {
      title: trans("Common.status"),
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const currentStatus = statusUpdates[record.key] || status;

        const isBackendControlled = backendOptions.some(
          (option) => option.value === currentStatus
        );
        let statusColor = "#D3F8E2";

        if (isBackendControlled) {
          const selectedBackendOption = backendOptions.find(
            (option) => option.value === currentStatus
          );
          if (selectedBackendOption) {
            statusColor = selectedBackendOption.color;
          }
        } else {
          const selectedOption = manualOptions.find(
            (option) => option.value === currentStatus
          );
          statusColor = selectedOption ? selectedOption.color : statusColor;
        }

        return (
          <div
            className={`custom-select-wrapper ${
              isBackendControlled ? "backend-controlled" : ""
            }`}
          >
            <Select
              value={currentStatus}
              disabled={isBackendControlled}
              onChange={(value) => {
                handleStatusChange(value, record.key);
              }}
              className="custom-select dropdown-style"
              popupClassName="custom-select-dropdown"
              style={{
                backgroundColor: `${statusColor}`,
                color: isBackendControlled ? "#888" : "black",
                cursor: isBackendControlled ? "not-allowed" : "pointer",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {isBackendControlled ? (
                <Option
                  key={currentStatus}
                  value={currentStatus}
                  className="backend-controlled"
                >
                  {currentStatus}
                </Option>
              ) : (
                manualOptions
                  .filter(
                    (option) =>
                      currentStatus !== "Offer sent" ||
                      option.value !== "New Lead"
                  )
                  .map((option) => (
                    <Option
                      key={option.value}
                      value={option.value}
                      className={`option-style custom-select-option ${
                        option.value === currentStatus ? "selected" : ""
                      }`}
                      style={{
                        backgroundColor: option.color,
                      }}
                    >
                      {option.label}
                    </Option>
                  ))
              )}
            </Select>
          </div>
        );
      },
    },
    {
      title: trans("Common.arrived"),
      dataIndex: "createdOn",
      key: "arrived",
      render: (createdOn) => {
        return createdOn ? formatDateTime(createdOn) : "-";
      },
    },
    {
      title: trans("Common.actions"),
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {record.status !== "Won" && (
            <>
              <Tooltip title={trans("Common.edit")}>
                <Button
                  type="button"
                  className="action-buttons"
                  onClick={() => {
                    // Track edit button click
                    appInsights.trackEvent({
                      name: "LeadPage_Edit_Button_Clicked",
                      properties: {
                        coachId: coachId,
                        leadId: record.key,
                        leadName: record.name,
                        timeOnPage: `${((Date.now() - pageLoadTime) / 1000).toFixed(2)}s`,
                      },
                    });
                    navigate(`/leads/editLead/${record.key}`, { state: record });
                  }}
                >
                  <i className="bi bi-pencil"></i>
                </Button>
              </Tooltip>
              <Tooltip title={trans("Common.delete")}>
                <Button
                  type="button"
                  className="action-buttons"
                  onClick={(e) => handleDeleteClick(record, e)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];
  const getShowingText = () => {
    const { current, pageSize } = pagination;
    const start = (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, totalCount);
    return totalCount
      ? `${trans("Common.showing")} ${start} ${trans(
          "Common.to"
        )} ${end} ${trans("Common.of")} ${totalCount} ${trans(
          "Common.results"
        )}`
      : trans("Common.noResults");
  };

  const sortMenuItems = [
    { key: "desc", label: trans("Common.newest") },
    { key: "asc", label: trans("Common.oldest") },
  ];

  return (
    <div className="lead-page">
      {/* Header Section */}

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-container">
          <div className="filter-header">
            <i className="bi bi-filter-left"></i>
            <span className="filter-text">Filter:</span>
          </div>
          <Menu
            mode="horizontal"
            selectedKeys={[statusFilter || "All"]}
            onClick={handleStatusFilterChange}
            className="filter-menu custom-menu"
            items={menuItems}
          />

          <div className="button-inside-menu">
            <Space>
              <Button
                className="common-theme-button-empty"
                onClick={handleSendOffer}
              >
                <i className="bi bi-send icon-blue-colour"></i>{" "}
                <span className="icon-blue-colour">
                  {trans("LeadPage.sendOffer")}
                </span>
              </Button>
              <Button
                className="common-theme-button"
                onClick={handleAddNewLead}
              >
                <i className="bi bi-plus"></i> {trans("LeadPage.createLead")}
              </Button>
            </Space>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-container">
        <Row className="pb-3 d-flex">
          <Col xs={4}>
            <Input
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={trans("Common.searchByName")}
              allowClear
              className="rounded-0 search-box common-theme-colour "
            />
          </Col>

          <Col
            xs="auto"
            className="ms-auto d-flex justify-content-end filter-lead"
          >
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                className="filter-dropdown-button common-theme-button"
              >
                <i className="bi bi-filter-left"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {sortMenuItems.map((item) => (
                  <Dropdown.Item
                    key={item.key}
                    onClick={() => handleSortChange({ key: item.key })}
                    className={sortOrder === item.key ? "bg-light" : ""}
                  >
                    {item.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
        <Row className=" align-items-center justify-content-between">
          <Col xs="auto">
            {selectedRowKeys.length > 0 && (
              <Button
                className="deleteButton"
                onClick={handleMultipleDeleteClick}
              >
                <i className="bi bi-trash"></i> {trans("Common.delete")}
              </Button>
            )}
          </Col>
        </Row>
        <Table
          className="custom-table"
          columns={columns}
          dataSource={leads}
          loading={loading}
          locale={{
            emptyText: "No leads available",
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            position: ["bottomRight"],
            showTotal: () => (
              <div className="showing-text">
                <Text>{getShowingText()}</Text>
              </div>
            ),
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: (e) => {
              handleRowClick(record, e);
            },
          })}
          rowClassName="clickable-row"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
            getCheckboxProps: (record) => ({
              disabled: record.status === "Won",
            }),
          }}
          bordered={false}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                }}
              />
            ),
            header: {
              cell: (props) => {
                return (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      background: "transparent",
                      borderColor: "#FCFBFFCC",
                      borderStyle: "solid",
                      fontFamily: "Proxima-Nova-Regular",
                      borderWidth: "8px 0px",
                      padding: "12px 16px",
                      // Border radius for header cells
                      borderRadius:
                        props.children === columns[0].title
                          ? "12px 0 0 12px"
                          : props.children === columns[columns.length - 1].title
                          ? "0 12px 12px 0"
                          : "0",
                    }}
                  />
                );
              },
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    fontFamily: "Proxima-Nova-Regular",
                    background: "transparent",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    padding: "12px 16px",
                    // Border radius for body cells
                    borderRadius:
                      props.index % columns.length === 0
                        ? "12px 0 0 12px"
                        : props.index % columns.length === columns.length - 1
                        ? "0 12px 12px 0"
                        : "0",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    margin: "10px 0",
                  }}
                />
              ),
            },
          }}
        />
      </div>

      {/* Modal for Deletion */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {trans("Common.confirmation")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{trans("Common.areYouSureDeleteLead")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleModalClose}
            className="back_button w-25 p-1"
          >
            {trans("Common.cancel")}
          </Button>
          <Button
            className="saveButton w-25 ml-2"
            onClick={handleConfirmDelete}
          >
            {trans("Common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && toastConfig.type === "success" && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default LeadPage;